// createDocumentPayment.js
//
// Create DOCUMENT_PAYMENT nodes and edge them to Invoice or Proposal.

import { createNode as createNodeFromFactory } from 'utilities/NodeFactory';
import { buildHierarchyEdgeOptions } from 'utilities/edgeHierarchy';
import { stampExclusiveAncestry } from 'utilities/nodeAncestry';
import { getResolvedFields } from 'utilities/NodeType';
import { readNodeFieldRawValue } from 'utilities/NodeFieldData';
import { getNodeType } from 'services/nodetypes/NodeTypesCacheService';
import { upsertEdge } from 'services/nodes/NodeEdgeService';
import { setNodeDocs } from 'services/nodes/NodeWriteService';
import {
  DOCUMENT_PAYMENT_TYPE_KEY,
  isDocumentPaymentParentTypeKey,
} from 'nodetypes/documents/payments/documentPaymentKeys';

function normalizeString(value) {
  return String(value || '').trim();
}

function normalizeTypeKey(value) {
  return normalizeString(value).toUpperCase();
}

function readParentField(parentNode, fieldName, parentTypeKey) {
  return readNodeFieldRawValue(
    parentNode,
    { name: fieldName, source: String(parentTypeKey || '').toLowerCase() },
    parentTypeKey,
  );
}

/**
 * @param {object|null|undefined} paymentType
 * @param {string} fieldName
 * @returns {string}
 */
function resolvePaymentSectionKey(paymentType, fieldName) {
  const fields = Array.isArray(paymentType?.schema?.fields)
    ? paymentType.schema.fields
    : [];
  const resolved = getResolvedFields(DOCUMENT_PAYMENT_TYPE_KEY, { includeHidden: true }) || [];
  const all = [...fields, ...resolved];
  const fieldDef = all.find((field) => normalizeString(field?.name || field?.key) === fieldName);
  const source = normalizeString(
    fieldDef?.source || paymentType?.key || DOCUMENT_PAYMENT_TYPE_KEY,
  ).toLowerCase();
  if (paymentType?.dataSection && (!fieldDef?.source || fieldDef.source === paymentType.key)) {
    return paymentType.dataSection;
  }
  return source
    ? `data_${source}`
    : (paymentType?.dataSection || 'data_document_payment');
}

/**
 * Schedule a new DOCUMENT_PAYMENT on an Invoice or Proposal.
 *
 * @param {object} options
 * @param {object} options.parentNode
 * @param {object} options.user
 * @param {string|null} [options.baseKey]
 * @param {object|null} [options.paymentType]
 * @param {object|null} [options.parentType]
 * @param {number} [options.existingCount=0]
 * @param {number|null} [options.defaultAmountCents]
 * @param {number|null} [options.percentOfTotal]
 * @param {'fixed'|'percent'} [options.amountBasis='fixed']
 * @param {string|null} [options.title]
 * @param {number|null} [options.sortOrder]
 * @param {string|null} [options.paymentTerms]
 * @returns {Promise<object>} Created payment node
 */
export async function createDocumentPayment({
  parentNode,
  user,
  baseKey = null,
  paymentType = null,
  parentType = null,
  existingCount = 0,
  defaultAmountCents = null,
  percentOfTotal = null,
  amountBasis = 'fixed',
  title = null,
  sortOrder = null,
  paymentTerms = null,
}) {
  if (!parentNode?.key || !user?.key) {
    throw new Error('Parent document and user are required to schedule a payment.');
  }

  const parentTypeKey = normalizeTypeKey(parentType?.key || parentNode?.type);
  if (!isDocumentPaymentParentTypeKey(parentTypeKey)) {
    throw new Error('Document payments can only be scheduled on Invoice or Proposal.');
  }

  const resolvedBaseKey = baseKey || parentNode.baseKey || null;
  const resolvedPaymentType = paymentType || getNodeType(DOCUMENT_PAYMENT_TYPE_KEY);

  const payment = await createNodeFromFactory(
    DOCUMENT_PAYMENT_TYPE_KEY,
    user,
    resolvedBaseKey || null,
  );

  const nextIndex = Math.max(0, Number(existingCount) || 0) + 1;
  payment.title = normalizeString(title) || `Payment ${nextIndex}`;

  const sectionWrites = {};
  const writeField = (fieldName, value) => {
    if (value === undefined || value === null || value === '') return;
    const sectionKey = resolvePaymentSectionKey(resolvedPaymentType, fieldName);
    if (!sectionWrites[sectionKey]) sectionWrites[sectionKey] = {};
    sectionWrites[sectionKey][fieldName] = value;
  };

  writeField('status', 'scheduled');
  writeField('source', 'manual');
  writeField('amountBasis', amountBasis === 'percent' ? 'percent' : 'fixed');

  if (defaultAmountCents != null && Number.isFinite(Number(defaultAmountCents))) {
    writeField('amount', Math.round(Number(defaultAmountCents)));
  }

  if (percentOfTotal != null && Number.isFinite(Number(percentOfTotal))) {
    writeField('percentOfTotal', Number(percentOfTotal));
  }

  const terms = paymentTerms != null && paymentTerms !== ''
    ? paymentTerms
    : readParentField(parentNode, 'paymentTerms', parentTypeKey);
  if (terms != null && terms !== '') writeField('paymentTerms', terms);

  const dueDate = readParentField(parentNode, 'dueDate', parentTypeKey);
  if (dueDate != null && dueDate !== '') writeField('dueDate', dueDate);

  const resolvedSortOrder = sortOrder != null && Number.isFinite(Number(sortOrder))
    ? Number(sortOrder)
    : nextIndex * 10;
  writeField('sortOrder', resolvedSortOrder);

  Object.entries(sectionWrites).forEach(([sectionKey, values]) => {
    payment[sectionKey] = {
      ...(payment[sectionKey] && typeof payment[sectionKey] === 'object' ? payment[sectionKey] : {}),
      ...values,
    };
  });

  const stamped = stampExclusiveAncestry(payment, parentNode, resolvedPaymentType);
  await setNodeDocs([{ key: stamped.key, payload: stamped }], { merge: false });
  await upsertEdge(
    parentNode.key,
    stamped.key,
    buildHierarchyEdgeOptions({
      parentNode,
      childNode: stamped,
      baseKey: resolvedBaseKey,
    }),
  );

  return stamped;
}
